import { useEffect, useRef, useCallback } from "react";

/**
 * Hook that monitors user activity and triggers a callback after a period of inactivity
 * @param {number} timeoutMinutes - Number of minutes of inactivity before timeout
 * @param {function} onTimeout - Callback function to execute when timeout occurs
 */
const useInactivityTimer = (timeoutMinutes, onTimeout) => {
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const resetTimer = useCallback(() => {
    const now = Date.now();

    // Debounce: only reset if more than 1 second since last activity
    if (now - lastActivityRef.current < 1000) {
      return;
    }

    lastActivityRef.current = now;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Start new timeout
    timeoutRef.current = setTimeout(() => {
      onTimeout();
    }, timeoutMinutes * 60 * 1000);
  }, [timeoutMinutes, onTimeout]);

  useEffect(() => {
    // Activity events to monitor
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    // Start initial timer
    resetTimer();

    // Cleanup on unmount
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimer]);
};

export default useInactivityTimer;
